<div>
  <vw-header-padding-safe-area></vw-header-padding-safe-area>
  <!-- La page défile d'un seul tenant : le titre, le climat et le gel, puis
       l'en-tête collant (recherche, filtres, mois, axe des mois) et la liste. -->
  <div class="pb-8 flex flex-col">
    <!-- La bande de la barre d'état reste couverte : les lignes ne passent pas dessous en défilant.
         Fixe et à sa hauteur exacte, elle ne cache jamais le titre ni le climat. -->
    <div class="calendar-status-band pointer-events-none fixed inset-x-0 top-0 z-10 bg-vw-bg" [style.height.px]="stickyTop"
      aria-hidden="true" data-test="calendar-band"></div>
    <header class="px-4">
      <h1 class="font-display text-display text-vw-ink">{{ 'calendar.title' | translate }}</h1>
    </header>

    <div class="px-4 mt-2 flex flex-col">
      <vw-climate-banner
        [selectedClimateType]="selectedClimateType"
        [myClimateType]="myClimateType"
        [city]="city"
        (pickerClick)="isClimatePickerOpen = true">
      </vw-climate-banner>
      <!-- Les dates de gel de l'adresse (CJ-04) ; la feuille dit d'où elles viennent. -->
      @if (hasFrostLine) {
        <button type="button" class="frost-line flex w-full items-start gap-2 min-h-target py-1 text-left text-body-s text-vw-ink"
          (click)="isFrostSheetOpen = true">
          <svg class="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <path d="M12 2v20M4.9 7l14.2 10M4.9 17 19.1 7M9 4l3 3 3-3M9 20l3-3 3 3" />
          </svg>
          <span class="grow min-w-0">
            {{ frostLineKey | translate: { last: frostDay(frostDates?.lastSpringFrost), first: frostDay(frostDates?.firstAutumnFrost) } }}
          </span>
          <svg class="mt-0.5 h-4 w-4 shrink-0 text-vw-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
          </svg>
        </button>
      }
    </div>

    <div class="calendar-sticky sticky z-20 mt-2 bg-vw-bg pb-1 pt-2 border-b border-vw-line" [style.top.px]="stickyTop">
      <div class="px-4">
        <vw-text-input [value]="searchValue" (valueChange)="onSearch($event)" icon="search" [prefix]="prefix"
          placeholder="agenda.search.placeholder">
          <ng-template #prefix>
            <div class="w-5 h-5 flex items-center justify-center text-vw-muted">
              <vw-plant-icon></vw-plant-icon>
            </div>
          </ng-template>
        </vw-text-input>
      </div>
      <!-- Filtres : quatre choix courts, donc un contrôle segmenté sur une ligne (pas de feuille à ouvrir) ;
           « Tout » montre ce qui se fait dans le mois choisi. -->
      <vw-choice-selector class="calendar-filters mt-2 block px-4" variant="segmented" [options]="filterOptions" [value]="filter"
        (valueChange)="setFilter($event)" [ariaLabel]="'calendar.filters.label' | translate"></vw-choice-selector>
      @if (hasRows) {
        <!-- L'axe des mois est le seul choix du mois : toucher une lettre, ou glisser le doigt dessus
             comme sur l'index A-Z d'un répertoire. Même colonne du nom (w-36) et mêmes marges que les
             cartes ; le petit trait du bas est aujourd'hui, prolongé par celui de la liste. -->
        <div class="calendar-axis mx-4 mt-2 flex gap-2 border-x border-transparent px-2">
          <span class="w-36 shrink-0"></span>
          <div class="calendar-axis__months relative grid grow touch-pan-y grid-cols-12" role="group"
            [attr.aria-label]="'calendar.month.pick' | translate"
            (pointerdown)="onAxisPointerDown($event)" (pointermove)="onAxisPointerMove($event)"
            (pointerup)="isScrubbing = false" (pointercancel)="isScrubbing = false">
            @for (m of monthNumbers; track m) {
              <button type="button" class="calendar-axis__month flex h-target min-w-0 items-center justify-center text-caption"
                [disabled]="isSearching" [attr.aria-pressed]="isSearching ? null : m === month"
                [attr.aria-current]="m === currentMonth ? 'date' : null"
                [attr.aria-label]="m === currentMonth ? ('calendar.month.current' | translate: { month: monthLabel(m) }) : monthLabel(m)"
                (click)="onAxisMonthClick(m, $event)">
                @let selected = m === month && !isSearching;
                <span class="flex h-6 w-full max-w-6 items-center justify-center rounded-full" aria-hidden="true"
                  [class.bg-vw-primary]="selected" [class.text-vw-on-color]="selected"
                  [class.font-semibold]="selected || m === currentMonth"
                  [class.text-vw-ink]="!selected && m === currentMonth" [class.text-vw-muted]="!selected && m !== currentMonth"
                  >{{ ('common.months.' + m | translate).slice(0, 1) }}</span>
              </button>
            }
            <span class="calendar-axis__today pointer-events-none absolute bottom-0 h-1.5 w-0.5 -translate-x-1/2 rounded-full bg-vw-ink"
              [style.left.%]="todayLeft" aria-hidden="true"></span>
          </div>
        </div>
      }
    </div>

    @if (hasRows) {
      <div class="mt-2 px-4">
        <vw-calendar-period-legend></vw-calendar-period-legend>
      </div>
    }

    <!-- Mois creux : le prochain mois chargé, à un toucher. -->
    @if (!isSearching && busyMonth) {
      <div class="quiet-month mx-4 mt-3 flex items-start gap-3 rounded-2xl bg-vw-surface-tint p-4">
        <span class="text-title-m" aria-hidden="true">🗓️</span>
        <div class="flex flex-col gap-2">
          <p class="text-body-s text-vw-ink">
            <b class="font-semibold">{{ 'calendar.quiet.title' | translate: { month: monthLabel(month) } }}</b>
            {{ 'calendar.quiet.next' | vwPlural: busyMonth.count : { month: monthLabel(busyMonth.month) } }}
          </p>
          <button type="button" class="self-start min-h-target rounded-full border-2 border-vw-primary bg-vw-surface px-4 text-body-s font-semibold text-vw-primary"
            (click)="goToMonth(busyMonth.month)">
            {{ 'calendar.quiet.see' | translate: { month: monthLabel(busyMonth.month) } }}
          </button>
        </div>
      </div>
    }

    @if (isSearching && !isLoading && !loadError && rows.length === 0) {
      <!-- Recherche sans résultat : dire pourquoi et proposer une sortie. -->
      <vw-empty-state class="search-empty block" titleKey="calendar.search.empty.title" [titleParams]="{ query: searchValue }"
        messageKey="calendar.search.empty.detail" actionKey="calendar.search.empty.clear" (action)="clearSearch()">
        <vw-search-icon icon />
      </vw-empty-state>
    } @else if (!isLoading && !loadError && rows.length === 0 && selectedClimateType) {
      <!-- Un climat exploré sans données. -->
      <vw-empty-state willy="dontknow" titleKey="calendar.climate.empty-title" messageKey="calendar.climate.empty"
        actionKey="calendar.climate.backToMine" actionLook="primary" (action)="onClimateSelected(null)"></vw-empty-state>
    } @else {
      <vw-calendar-hierarchies
        [hierarchies]="rows" [isLoading]="isLoading" [loadError]="loadError"
        [badgeOf]="badgeOf" (cultivationSelected)="onCultivationSelected($event)" (retry)="retry()">
      </vw-calendar-hierarchies>
    }

    <vw-cultivation-guide-toast [isOpen]="isShowingGuide" [cultivation]="selectedCultivation"
      [cultivationVariant]="selectedVariant" (close)="isShowingGuide = false">
    </vw-cultivation-guide-toast>
    <vw-frost-dates-toast [isOpen]="isFrostSheetOpen" [frostDates]="frostDates" [city]="city" (close)="isFrostSheetOpen = false">
    </vw-frost-dates-toast>
    <vw-climate-info-toast [isOpen]="isClimateInfoOpen" (close)="isClimateInfoOpen = false"></vw-climate-info-toast>
    <vw-climate-picker-toast
      [isOpen]="isClimatePickerOpen"
      [availableClimates]="availableClimates"
      [selectedClimateType]="selectedClimateType"
      [myClimateType]="myClimateType"
      (close)="isClimatePickerOpen = false"
      (climateSelected)="onClimateSelected($event)"
      (infoClick)="isClimateInfoOpen = true">
    </vw-climate-picker-toast>
  </div>
</div>
